import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import ReactQuill from "react-quill";
import Select from "react-select";
import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import { Row, Col, Card, Button, Space, Typography, Table, Input, Modal, Tag, Tooltip, Checkbox, Upload, Tabs, Popover, Popconfirm, Image, Progress, Steps, List } from "antd";
import ImgCrop from "antd-img-crop";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBinoculars, faQuestionCircle, faUpload } from "@fortawesome/free-solid-svg-icons";
import ReactToPrint from "react-to-print";
import _ from "lodash";

const aspect_ratios = [
  { label: "Square (1:1)", ratio: 1 / 1, value: 0 },
  { label: "Rectangle (4:1)", ratio: 4 / 1, value: 1 },
];

const timer_rounding_intervals = [
  { label: "Whole Hour", value: "hour" },
  { label: "Half Hour", value: "half-hour" },
  { label: "Quarter Hour", value: "quarter-hour" },
  { label: "Tenth Hour", value: "tenth-hour" },
  { label: "To The Minute", value: "no rounding" },
];

const modules = {
  toolbar: [[{ header: [2, 3, 4, false] }], ["bold", "italic", "underline"], [{ list: "ordered" }, { list: "bullet" }], ["link"], [{ align: [false, "center", "right"] }], ["clean"]],
};
const formats = ["header", "bold", "italic", "underline", "strike", "blockquote", "list", "bullet", "indent", "link", "image", "align"];

function CompanySettings(props) {
  const [company, setCompany] = useState(null);
  const [billingInformation, setBillingInformation] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [invoice, setInvoice] = useState(null);
  const invoiceRef = useRef();
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);

  const [jobStatuses, setJobStatuses] = useState([]);
  const [taskStatuses, setTaskStatuses] = useState([]);
  const [companyLogoProcessing, setCompanyLogoProcessing] = useState(false);

  const [qboStep, setQboStep] = useState(0);
  //0,1,2 - nolink,inprogress,linked
  const [qboStatuses, setQboStatuses] = useState({ link_status: "nolink", accounts_status: "nolink", clients_status: "nolink" });
  const [qboAccounts, setQboAccounts] = useState([]);
  const [qboAccountsModalOpen, setQboAccountsModalOpen] = useState(false);
  const [qboClients, setQboClients] = useState([]);
  const [ftClients, setFtClients] = useState([]);
  const [qboClientsModalOpen, setQboClientsModalOpen] = useState(false);
  const [qboItems, setQboItems] = useState([]);
  const [items, setItems] = useState([]);
  const [qboItemsModalOpen, setQboItemsModalOpen] = useState(false);
  const [modalWidth, setModalWidth] = useState(1600);

  const GetCompany = async () => {
    let response = await props.AxiosRequest("/api/companies/get");
    setCompany({
      ...response.data.company,
      forte_org_id: response.data.company.merchant_organization_id,
      forte_loc_id: response.data.company.merchant_location_id,
      forte_api_login: response.data.company.merchant_api_login_credentials,
      forte_enrolled: response.data.company.merchant_organization_id && response.data.company.merchant_location_id && response.data.company.merchant_api_login_credentials,
    });
    if (response.data.company?.qbo_enabled === 1) setQboStep(qbo_steps.length - 1);
  };
  const OnCompanyLogoChange = (info) => {
    if (info.file.status === "uploading") return setCompanyLogoProcessing(true);
    if (info.file.status === "done") GetCompany();
  };
  const BeforeCompanyLogoUpload = (file) => {
    if (!(file.type === "image/jpeg" || file.type === "image/png")) {
      props.MakeNotification("error", "You can only upload JPG/PNG file!");
      return false;
    }
    if (file.size / 1000.0 > 5000) {
      props.MakeNotification("error", "File too big. Please try a smaller file.");
      return false;
    }
    return true;
  };
  const SaveCompanySettings = async () => {
    await props.AxiosRequest("/api/companies/update", { ...company });
    GetCompany();
  };
  const SaveCompanyMiscSettings = async () => {
    await props.AxiosRequest("/api/companies/update/misc", { ...company });
    await props.AxiosRequest(
      "/api/task-statuses/update",
      taskStatuses.find((f) => f.start),
    );
    await props.AxiosRequest(
      "/api/task-statuses/update",
      taskStatuses.find((f) => f.end),
    );
    await props.AxiosRequest(
      "/api/job-statuses/update",
      jobStatuses.find((f) => f.start),
    );
    await props.AxiosRequest(
      "/api/job-statuses/update",
      jobStatuses.find((f) => f.end),
    );
    GetCompany();
  };

  const GetCompanyPaymethodInfo = async () => {
    let response = await props.AxiosRequest("/api/forte/get-payment-info");

    if (!response.data.data || Object.keys(response.data.data).length === 0) return setBillingInformation(null);
    setBillingInformation({
      name_on_card: response.data.data.card.name_on_card,
      card_number: "**** **** **** " + response.data.data.card.last_4_account_number,
      expire_month: "**",
      expire_year: "****",
      cvv: "***",
      loaded: true,
      has_been_edited: false,
    });
  };
  const SaveBillingInfo = async () => {
    if (billingInformation.card_number.length < 12 || billingInformation.card_number.startsWith("*")) return props.MakeNotification("warning", "Please make sure to enter all 12 digits of the card.");
    if (billingInformation.expire_month.length < 2 || billingInformation.expire_month.startsWith("*"))
      return props.MakeNotification("warning", "Please make sure to enter the expiration month in the two-digit format.");
    if (billingInformation.expire_year.length < 4 || billingInformation.expire_year.startsWith("*"))
      return props.MakeNotification("warning", "Please make sure to enter the expiration year in the four-digit format.");

    await props.AxiosRequest("/api/forte/update-payment-info", { billing_info: { ...billingInformation } });
    GetCompanyPaymethodInfo();
  };
  const DeleteBillingInfo = async () => {
    await props.AxiosRequest("/api/forte/remove-payment-info");
    GetCompanyPaymethodInfo();
  };

  const GetCompanyInvoices = async () => {
    let response = await props.AxiosRequest("/api/company-invoices/get/all");
    setInvoices(response.data.invoices);
  };
  const PayInvoice = async (id) => {
    await props.AxiosRequest("/api/company-invoices/pay", { id });
    GetCompanyInvoices();
  };

  const GetTaskStatuses = async () => {
    let response = await props.AxiosRequest("/api/task-statuses/get");
    setTaskStatuses(
      response.data.task_statuses.map((m) => {
        return { ...m, label: m.name, value: m.id };
      }),
    );
  };
  const GetJobStatuses = async () => {
    let response = await props.AxiosRequest("/api/job-statuses/get");
    setJobStatuses(
      response.data.job_statuses.map((m) => {
        return { ...m, label: m.name, value: m.id };
      }),
    );
  };
  const InitiateQBOLink = async () => {
    setQboStatuses({ ...qboStatuses, link_status: "inprogress" });
    let response = await props.AxiosRequest("/api/quickbooks/link");
    if (response.data.status === "success") window.open(response.data.redirect_uri, "_blank");

    let interval_id = setInterval(async () => {
      let response2 = await props.AxiosRequest("/api/companies/get");
      if (response2.data.status === "success" && response2.data.company.qbo_enabled === 1) {
        clearInterval(interval_id);
        setQboStatuses({ ...qboStatuses, link_status: "linked" });
      }
    }, 3000);
  };
  const PullAccountsFromQBO = async () => {
    setQboStatuses({ ...qboStatuses, accounts_status: "nolink" });
    let response = await props.AxiosRequest("/api/quickbooks/get-qbo-accounts");
    setQboAccounts(response.data.qbo_accounts);
    setQboAccountsModalOpen(true);
  };
  const PullClientsFromQBO = async () => {
    setQboStatuses({ ...qboStatuses, clients_status: "nolink" });
    let response = await props.AxiosRequest("/api/quickbooks/get-qbo-clients");
    setQboClients(response.data.qbo_clients);
    setFtClients(response.data.ft_clients);
    setQboClientsModalOpen(true);
  };
  const PullItemsFromQBO = async () => {
    let deliquents = [
      ...qboItems.filter((f) => typeof items.find((ftf) => f.processed_name === ftf.processed_name) == "undefined"),
      ...items.filter((f) => typeof qboItems.find((ftf) => f.processed_name === ftf.processed_name) == "undefined"),
    ];
    if (deliquents.length > 0) return props.MakeNotification("warning", "All Items Must be in the left column before linking can occur. ");
    setQboStatuses({ ...qboStatuses, items_status: "nolink" });
    let response = await props.AxiosRequest("/api/quickbooks/get-qbo-items");
    setQboItems(response.data.qbo_items);
    let response_items = await props.AxiosRequest("/api/items/get");
    setItems(
      response_items.data.items.map((m) => {
        return { ...m, processed_name: m.name.toLowerCase().replace(/[\s,.]/g, "") };
      }),
    );
    setQboItemsModalOpen(true);
  };
  const UpdateItemsFromQBO = async () => {
    let response = await props.AxiosRequest("/api/quickbooks/update-items-from-qbo");
    if (response.data.status === "success") setQboItemsModalOpen(false);
  };
  const ImportQBOAccounts = async () => {
    let response = await props.AxiosRequest("/api/quickbooks/import-qbo-accounts");
    if (response.data.status === "success") setQboAccountsModalOpen(false);
  };
  const ImportQBOClients = async () => {
    let response = await props.AxiosRequest("/api/quickbooks/import-qbo-clients");
    if (response.data.status === "success") setQboClientsModalOpen(false);
  };
  const DisconnectQBO = async () => {
    await props.AxiosRequest("/api/quickbooks/disconnect");
    GetCompany();
  };

  const ValidateMerchantOrganizationAndLocation = async () => {
    await props.AxiosRequest("/api/companies/validate/forte", { org_id: company?.forte_org_id, loc_id: company?.forte_loc_id, api: company?.forte_api_login });
    GetCompany();
  };

  useEffect(() => {
    GetCompany();
    GetCompanyPaymethodInfo();
    GetCompanyInvoices();
    GetTaskStatuses();
    GetJobStatuses();
  }, []);

  useEffect(() => {
    const resize = () => {
      const maxSize = Math.min(1600, window.innerWidth);
      setModalWidth(maxSize);
    };
    window.addEventListener("resize", resize);
  });
  const qbo_steps = [
    {
      key: 0,
      title: "Step One",
      content: (
        <>
          <Row gutter={[16, 16]}>
            <Col span={24} className="text-center">
              <Typography.Title level={4}>First, Log Into QuickBooks</Typography.Title>
              <p>Click the button below to be directed to QuickBooks's login page. From there you will be asked to add 'FirmTamer Link' to your QuickBooks account.</p>
              <Button type="primary" disabled={!qboStatuses.link_status === "nolink"} onClick={InitiateQBOLink}>
                {qboStatuses.link_status === "inprogress" ? <FontAwesomeIcon icon="spinner" spin /> : "Link to QuickBooks Online"}
              </Button>
              <br />
              <br />
              {qboStatuses.link_status === "linked" ? <Typography.Title level={3}>Success! Click Next To Continue</Typography.Title> : null}
            </Col>
          </Row>
        </>
      ),
    },
    {
      key: 1,
      title: "Step Two",
      content: (
        <>
          <Row gutter={[16, 16]}>
            <Col span={24} className="text-center">
              <Typography.Title level={4}>Next, Let's Pull Your Accounts</Typography.Title>
              <p>Click the button below to be shown your QuickBooks Online Accounts that we will pull. From there follow the instructions in the pop-up.</p>
              <Button type="primary" onClick={PullAccountsFromQBO} disabled={!qboStatuses.accounts_status === "nolink"}>
                {qboStatuses.accounts_status === "inprogress" ? <FontAwesomeIcon icon="spinner" spin /> : "View Accounts We Will Pull from QuickBooks Online"}
              </Button>
              <br />
              <small>Note: We pull your accounts so that your items can be linked to the proper account. They will not be editable in FirmTamer, only readable.</small>
            </Col>
          </Row>
        </>
      ),
    },
    {
      key: 2,
      title: "Step Three",
      content: (
        <>
          <Row gutter={[16, 16]}>
            <Col span={24} className="text-center">
              <Typography.Title level={4}>Next, Let's Link Your Clients</Typography.Title>
              <p>Click the button below to be shown which clients are ready to link, and which clients are not currently matched up. From there follow the instructions in the pop-up.</p>
              <Button type="primary" onClick={PullClientsFromQBO} disabled={!qboStatuses.clients_status === "nolink"}>
                {!qboStatuses.clients_status === "inprogress" ? <FontAwesomeIcon icon="spinner" spin /> : "Verify Client Link from QuickBooks Online"}
              </Button>
            </Col>
          </Row>
        </>
      ),
    },
    {
      key: 3,
      title: "Step Four",
      content: (
        <>
          <Row gutter={[16, 16]}>
            <Col span={24} className="text-center">
              <Typography.Title level={4}>Next, Let's Link Your Items</Typography.Title>
              <p> Click the button below to be shown which clients are ready to link, and which clients are not currently matched up. From there follow the instructions in the pop-up.</p>
              <Button type="primary" onClick={PullItemsFromQBO} disabled={!qboStatuses.items_status === "nolink"}>
                {!qboStatuses.items_status === "inprogress" ? <FontAwesomeIcon icon="spinner" spin /> : "Verify Item Link from QuickBooks Online"}
              </Button>
            </Col>
          </Row>
        </>
      ),
    },
    {
      key: 4,
      title: "Complete!",
      content: (
        <>
          <Row gutter={[16, 16]}>
            <Col span={24} className="text-center">
              <Typography.Title level={4}>All Done!</Typography.Title>
              <p>
                Now all your clients, invoices, and payments will be synced with QuickBooks Online! If you have any questions, please reach out to us at{" "}
                <a href="mailto:success@firmtamer.com">success@firmtamer.com</a> and we answer your questions.
              </p>
              <p>If you need to, you can always disconnect QuickBooks Online from your account, and reconnect it at anytime!</p>
            </Col>
          </Row>
        </>
      ),
    },
  ];
  const invoices_table_columns = [
    {
      title: "Invoice Number",
      dataIndex: "id",
      key: "id",
      sorter: (a, b) => (a.id > b.id ? 1 : -1),
      render: (value) => "#" + value,
    },
    {
      title: "Plan",
      dataIndex: "subscription_name",
      key: "subscription_name",
      sorter: (a, b) => (a.subscription_name > b.subscription_name ? 1 : -1),
    },
    {
      title: "Promo",
      dataIndex: "promo_name",
      key: "promo_name",
      sorter: (a, b) => (a.promo_name > b.promo_name ? 1 : -1),
    },
    {
      title: "Period",
      dataIndex: "start_date",
      key: "start_date",
      render: (value, object) => props.MakeDateTimeReadable(object.start_date) + " - " + props.MakeDateTimeReadable(object.end_date),
      sorter: (a, b) => (a.start_date > b.start_date ? 1 : -1),
      defaultSortOrder: "descend",
    },
    {
      title: "Full Price",
      dataIndex: "initial_price",
      key: "initial_price",
      render: (value) => props.FormatNumberToCurrency(value),
    },
    {
      title: "Discounts/Promos",
      dataIndex: "discount",
      key: "discount",
      render: (value) => props.FormatNumberToCurrency(value * -1),
    },
    {
      title: "Final Price",
      dataIndex: "final_price",
      key: "final_price",
      render: (value) => props.FormatNumberToCurrency(value),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (value, object) => {
        if (value === 1)
          return (
            <>
              <Tag className="mb-1">Unpaid</Tag>
            </>
          );
        return <Tag>Paid</Tag>;
      },
    },
    {
      title: "",
      align: "right",
      dataIndex: "id",
      key: "id",
      render: (value, object) => (
        <Space>
          <Button
            size="small"
            onClick={() => {
              let found_invoice = invoices.find((f) => f.id === value);
              setInvoice(found_invoice);
              setInvoiceModalOpen(true);
            }}
          >
            <FontAwesomeIcon icon={faBinoculars} className="mr-2" />
            View Invoice
          </Button>
          {object.status === 1 ? (
            <Popconfirm
              title={"Are you sure you want to charge the card on file " + props.FormatNumberToCurrency(object.final_price) + "?"}
              onConfirm={() => {
                PayInvoice(object.subscription_id);
              }}
            >
              <Button size="small" type="primary">
                Pay Now
              </Button>
            </Popconfirm>
          ) : null}
        </Space>
      ),
    },
  ];
  const company_setting_tabs = [
    {
      label: "Account Settings",
      key: 0,
      children: (
        <>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <label htmlFor="input_name">Company Name</label>
              <Input
                id="input_name"
                placeholder="Company Name"
                value={company?.name}
                onChange={(e) => {
                  setCompany({ ...company, name: e.target.value });
                }}
              />
            </Col>
            <Col span={4}></Col>
            <Col span={8} className="text-center">
              {company?.image_link ? (
                <Image
                  src={"https://firmtamer-public-bucket.s3.amazonaws.com/" + company?.image_link}
                  alt="avatar"
                  style={{
                    width: "100%",
                  }}
                />
              ) : (
                <div>
                  {companyLogoProcessing ? <LoadingOutlined /> : <PlusOutlined />}
                  <div
                    style={{
                      marginTop: 8,
                    }}
                  >
                    Upload
                  </div>
                </div>
              )}
            </Col>
            <Col span={8} className="text-center">
              <label>Company Logo Aspect Ratio</label>
              {console.log(company?.invoice_image_type)}
              <Select
                className="mb-3"
                value={aspect_ratios.find((ar) => ar.value === company?.invoice_image_type)}
                options={aspect_ratios}
                onChange={(v) => {
                  setCompany({ ...company, invoice_image_type: v.value });
                }}
              />
              <br />
              <br />
              <ImgCrop showGrid aspect={aspect_ratios.find((ar) => ar.value === company?.invoice_image_type)?.ratio || 1} minZoom={0.5} quality={1} cropperProps={{ restrictPosition: false }}>
                <Upload
                  action={window.location.protocol + "//" + window.location.hostname + (window.location.port === "3000" ? ":3001" : "") + "/api/companies/logo-update"}
                  accept=".jpg,.jpeg,.png"
                  name="logo_image"
                  headers={{ "x-access-token": props.authToken }}
                  data={{ "x-access-token": props.authToken, invoice_image_type: company?.invoice_image_type }}
                  className="avatar-uploader"
                  showUploadList={false}
                  onChange={OnCompanyLogoChange}
                >
                  <Button icon={<FontAwesomeIcon icon={faUpload} />}>Click to Upload a New Logo</Button>
                </Upload>
              </ImgCrop>
            </Col>
            <Col span={4}></Col>
            <Col span={12}>
              <label htmlFor="input_telephone">Phone</label>
              <Input
                id="input_telephone"
                placeholder="First name"
                value={company?.telephone}
                onChange={(e) => {
                  setCompany({ ...company, telephone: e.target.value });
                }}
              />
            </Col>
            <Col span={12}>
              <label htmlFor="input_fax">Fax</label>
              <Input
                id="input_fax"
                placeholder="Fax"
                value={company?.fax}
                onChange={(e) => {
                  setCompany({ ...company, fax: e.target.value });
                }}
              />
            </Col>
            <Col span={12}>
              <label htmlFor="input_email">Email</label>
              <input
                type="email"
                className="form-control"
                id="input_email"
                placeholder="Company Email"
                value={company?.email}
                onChange={(e) => {
                  setCompany({ ...company, email: e.target.value });
                }}
              />
            </Col>
            <Col span={12}>
              <label htmlFor="input_website">Website</label>
              <Input
                id="input_website"
                placeholder="Company Website"
                value={company?.website}
                onChange={(e) => {
                  setCompany({ ...company, website: e.target.value });
                }}
              />
            </Col>
            <Col span={16}>
              <label htmlFor="input_address_line_1">Address</label>
              <Input
                id="input_address_line_1"
                placeholder="1234 Main St"
                value={company?.address_line_1}
                onChange={(e) => {
                  setCompany({ ...company, address_line_1: e.target.value });
                }}
              />
            </Col>
            <Col span={8}>
              <label htmlFor="input_address_line_2">Address 2</label>
              <Input
                id="input_address_line_2"
                placeholder="Apartment, studio, or floor"
                value={company?.address_line_2}
                onChange={(e) => {
                  setCompany({ ...company, address_line_2: e.target.value });
                }}
              />
            </Col>
            <Col span={10}>
              <label htmlFor="input_city">City</label>
              <Input
                id="input_city"
                value={company?.city}
                onChange={(e) => {
                  setCompany({ ...company, city: e.target.value });
                }}
              />
            </Col>
            <Col span={8}>
              <label htmlFor="input_state">State</label>
              <Input
                id="input_state"
                value={company?.state}
                onChange={(e) => {
                  setCompany({ ...company, state: e.target.value });
                }}
              />
            </Col>
            <Col span={6}>
              <label htmlFor="input_zip">Zip</label>
              <Input
                id="input_zip"
                value={company?.zip}
                onChange={(e) => {
                  setCompany({ ...company, zip: e.target.value });
                }}
              />
            </Col>
            <div className="mb-3 col-md-12 text-right">
              <Button type="primary" id="save-company-information" onClick={SaveCompanySettings}>
                Save changes
              </Button>
            </div>
          </Row>
        </>
      ),
    },
    {
      label: "Billing Settings",
      key: 1,
      children: (
        <>
          <Card title={<Typography.Title level={5}>Card on File</Typography.Title>}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <label htmlFor="first_name_on_card">Cardholder's Name</label>
                <Input
                  type="text"
                  size="large"
                  id="first_name_on_card"
                  name="first_name_on_card"
                  value={billingInformation?.name_on_card}
                  onChange={(e) => {
                    setBillingInformation({ ...billingInformation, name_on_card: e.target.value, has_been_edited: true });
                  }}
                  placeholder="John Smith"
                />
              </Col>
              <Col span={12} />
              <Col span={12}>
                <label htmlFor="card_number">Card Number</label>
                <Input
                  id="card_number"
                  size="large"
                  value={billingInformation?.card_number}
                  onChange={(e) => {
                    setBillingInformation({ ...billingInformation, card_number: e.target.value, has_been_edited: true });
                  }}
                />
              </Col>
              <Col span={12} />
              <Col span={4}>
                <label htmlFor="card_expiry">Expiration Month</label>
                <Input
                  id="card_expiry"
                  size="large"
                  placeholder="MM"
                  value={billingInformation?.expire_month}
                  onChange={(e) => {
                    setBillingInformation({ ...billingInformation, expire_month: e.target.value, has_been_edited: true });
                  }}
                />
              </Col>
              <Col span={4}>
                <label htmlFor="card_expiry">Expiration Year</label>
                <Input
                  id="card_expiry"
                  size="large"
                  placeholder="YYYY"
                  value={billingInformation?.expire_year}
                  onChange={(e) => {
                    setBillingInformation({ ...billingInformation, expire_year: e.target.value, has_been_edited: true });
                  }}
                />
              </Col>
              <Col span={4}>
                <label htmlFor="card_expiry">CVV</label>
                <Input
                  id="card_cvv"
                  size="large"
                  placeholder="123"
                  value={billingInformation?.cvv}
                  onChange={(e) => {
                    setBillingInformation({ ...billingInformation, cvv: e.target.value, has_been_edited: true });
                  }}
                />
              </Col>
              <Col span={12} className="text-right">
                <label>&nbsp;</label>
                {billingInformation?.loaded ? (
                  <div>
                    <label>&nbsp;</label>
                    <Popconfirm title="Are you sure you want to delete this card?" onConfirm={DeleteBillingInfo}>
                      <Button danger primary>
                        Delete Card Information
                      </Button>
                    </Popconfirm>
                  </div>
                ) : null}
                {billingInformation?.has_been_edited ? (
                  <div>
                    <label>&nbsp;</label>
                    <Button primary onClick={SaveBillingInfo}>
                      Save Payment Information
                    </Button>
                  </div>
                ) : null}
              </Col>
            </Row>
          </Card>
          <Card title={<Typography.Title level={5}>Previous Invoices</Typography.Title>} className="mt-3">
            <Table className="table table-striped hover" columns={invoices_table_columns} dataSource={invoices} />
          </Card>
        </>
      ),
    },
    {
      label: "Merchant Settings",
      key: 2,
      children: (
        <>
          <Typography.Title level={5}>Merchant Settings</Typography.Title>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <label className="form-label">Status: &nbsp;</label>
              {company?.forte_enrolled ? (
                <Tag color="#87d068">Enrolled</Tag>
              ) : (
                <Tag color="#108ee9" inv>
                  Not Yet Enrolled
                </Tag>
              )}
              <br />
              <br />
              {company?.forte_enrolled ? (
                <>
                  <label className="col-form-label-sm">Payment Link</label>
                  <Input
                    type="text"
                    onClick={() => {
                      if (navigator?.clipboard) {
                        navigator.clipboard.writeText(
                          window.location.protocol + "//" + window.location.hostname + (window.location.port === "3000" ? ":3000" : "") + "/portal/payments-firm/f/" + company?.tag,
                        );
                        props.MakeNotification("success", "Link Copied to your clipboard");
                      }
                    }}
                    style={{ textAlign: "center" }}
                    value={
                      company?.tag ? window.location.protocol + "//" + window.location.hostname + (window.location.port === "3000" ? ":3000" : "") + "/portal/payments-firm/f/" + company?.tag : "N/A"
                    }
                    className="form-control"
                    readOnly
                  />
                </>
              ) : (
                <Link to="https://console.forte.net/app?token=wNL4SKedNU6utXNLDQgwKQ" target="_blank" rel="noreferrer noopener">
                  <Button type="primary">Get Enrolled Here!</Button>
                </Link>
              )}
              <hr />
              <br />
              <br />
              <br />
              <Typography.Title level={5}>After your application is approved, fill in these fields with the values Forte provides, and then validate them.</Typography.Title>
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <label className="form-label">Forte Organization ID</label>
                  <Input
                    value={company?.forte_org_id}
                    onChange={(e) => {
                      setCompany({ ...company, forte_org_id: e.taget.value });
                    }}
                  />
                </Col>
                <Col span={24}>
                  <label className="form-label">Forte Location ID</label>
                  <Input
                    value={company?.forte_loc_id}
                    onChange={(e) => {
                      setCompany({ ...company, forte_loc_id: e.taget.value });
                    }}
                  />
                </Col>
                <Col span={24}>
                  <label className="form-label">Forte API Login</label>
                  <Input
                    value={company?.forte_api_login}
                    onChange={(e) => {
                      setCompany({ ...company, forte_api_login: e.taget.value });
                    }}
                  />
                </Col>
                <Col span={24} className="text-right">
                  <Button type="primary" onClick={ValidateMerchantOrganizationAndLocation}>
                    Validate
                  </Button>
                </Col>
              </Row>
            </Col>
            <Col span={12}>
              <Card
                title={
                  <Row gutter={[16, 16]}>
                    <Col span={24} className="text-center">
                      <Typography.Title level={5}>Email Invoices, and Get Paid Online!</Typography.Title>
                    </Col>
                  </Row>
                }
                bordered={false}
                className="bg-light"
              >
                <p>
                  <b>Enroll with Forte Payment Systems and make it easy for your clients to pay their invoices.</b>
                  <br />
                  <br />
                  With Forte's integrated merchant services, your clients can simply click on the link in your invoice email to pay with their credit or debit card. It's quick, easy, and secure.
                  <br />
                  <br />
                  Forte's rates are very competitive:
                  <ul>
                    <li>2.79% + $0.25 per card transaction</li>
                    <li>$15 monthly account fee</li>
                  </ul>
                  These rates are likely less than you are currently paying, and they come with the added benefit of being integrated with your time and billing software.
                  <br />
                  <br />
                  With Forte, you can:
                  <ul>
                    <li>Export your invoices and payments to QuickBooks Desktop or QuickBooks Online</li>
                    <li>Receive real-time notifications of payments</li>
                    <li>Take Online Payments</li>
                    <li>Manage your account online</li>
                  </ul>
                  Enroll with Forte Payment Systems today and start accepting payments from your clients with ease.
                  <br />
                  <br />
                  And there are even more benefits to using Forte!
                  <ul>
                    <li>Forte is a PCI DSS Level 1 compliant payment processor, which means that your customers' data is safe and secure.</li>
                    <li>Forte offers a variety of payment options, including credit cards, debit cards, ACH payments, and wire transfers.</li>
                    <li>Forte has a dedicated customer support team that is available 24/7 to help you with any questions or problems.</li>
                  </ul>
                </p>
              </Card>
            </Col>
          </Row>

          {/* ///// */}
          <hr />
          {/* {this.state.forte_enrolled ? (
            <div className="row">
              <div className="col-md-12 text-center">
                <h3 className="mb-2">Now that you are enrolled...</h3>
                <p>
                  If you'd like a way to take payments online, you can link to this page{" "}
                  <Link to={{ pathname: "/portal/payments/" + this.state.company.tag }} target="_blank">
                    <b>here</b>
                  </Link>
                </p>
              </div>
              <div className="col-md-6 offset-md-3 text-center">
                {this.state.company.name}'s Link:
                <input
                  className="form-control"
                  value={window.location.protocol + "//" + window.location.hostname + (window.location.port === "3000" ? ":3000" : "") + "/portal/payments/" + this.state.company.tag}
                  onClick={() => {
                    navigator.clipboard.writeText(
                      window.location.protocol + "//" + window.location.hostname + (window.location.port === "3000" ? ":3000" : "") + "/portal/payments/" + this.state.company.tag,
                    );
                    this.props.MakeNotification("success", "Link copied to clipboard!");
                  }}
                />
              </div>
            </div>
          ) : null} */}
        </>
      ),
    },
    {
      label: "QuickBooks Online Settings",
      key: 3,
      children: (
        <>
          <Typography.Title level={5}>QuickBooks Online Settings</Typography.Title>
          <Row gutter={[16, 16]}>
            <Col span={4} />
            <Col span={16} className="bg-light text-center">
              <Typography.Title level={5} className="mt-3">
                Link FirmTamer with QuickBooks Online!
              </Typography.Title>
              <hr />
              <b>What does linking FirmTamer with QuickBooks Onine do?</b>
              <br />
              <br />
              <ul>
                <li>
                  Seamless client management: When you link the two platforms, your clients created in FirmTamer will automatically create clients in QuickBooks Online, and any updates made to clients
                  will be kept synced across both systems. This means that you can keep track of all of your clients in one place, and you won't have to worry about duplicating data.
                </li>
                <li>
                  Automated invoicing: Any finalized invoices created in FirmTamer will automatically be sent to QuickBooks Online. This saves you time and hassle, and it ensures that your invoices
                  are always up-to-date.
                </li>
                <li>
                  Accurate recordkeeping: Payments made through FirmTamer's payment processor, Forte, will also be sent to QuickBooks Online. If a specific invoice or customer link is used, the
                  payment will be put in the correct spot.
                </li>
              </ul>
              <br />
              <br />
              {company?.qbo_settings_shown ? null : (
                <>
                  <b>Ready to get started?</b>
                  <p className="m-5">
                    QuickBooks Online integration is a several step process. In order to help ensure it goes as smoothly as possible, please email us at{" "}
                    <a href="mailto:success@firmtamer.com">success@firmtamer.com</a> and we will schedule a time with you to go over the steps and get you integrated with QuickBooks Online!
                  </p>
                </>
              )}
            </Col>
            <Col span={4} />
            <Col span={24}>
              <hr />
            </Col>
            {company?.qbo_settings_shown ? (
              <>
                <Col span={24} className="mt-3">
                  <Steps current={qboStep} items={qbo_steps}></Steps>
                </Col>
                <Col span={24}>{qbo_steps[qboStep].content}</Col>
                <Col span={12}>
                  {qboStep > 0 && (
                    <Button
                      style={{
                        margin: "0 8px",
                      }}
                      onClick={() => setQboStep(qboStep - 1)}
                    >
                      Previous
                    </Button>
                  )}
                </Col>
                <Col span={12} className="text-right">
                  {qboStep < qbo_steps.length - 1 && (
                    <Button type="primary" onClick={() => setQboStep(qboStep + 1)}>
                      Next
                    </Button>
                  )}
                  {qboStep === qbo_steps.length - 1 && (
                    <Space>
                      <Popconfirm title="Are you sure?" description="Are you sure you want to disconnect QuickBooks Online from your FirmTamer account?" okText="Disconnect" onConfirm={DisconnectQBO}>
                        <Button type="primary" danger>
                          Disconnect QuickBooks Online
                        </Button>
                      </Popconfirm>
                      <Button type="primary" onClick={() => console.log("Processing complete!")}>
                        Done
                      </Button>
                    </Space>
                  )}
                </Col>
              </>
            ) : null}
          </Row>
        </>
      ),
    },
    {
      label: "Misc Settings",
      key: 4,
      children: (
        <>
          <Typography.Title level={5}>Miscellaneous Settings</Typography.Title>
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <Popover
                placement="top"
                content={
                  <div>
                    This will be the number of the next invoice created. <br />
                    If exporting to QuickBooks, make sure this is larger than any invoice number in QuickBooks.
                  </div>
                }
              >
                <label className="form-label">Next Invoice Number</label>

                <Input
                  type="number"
                  placeholder="Enter Invoice Number Here"
                  value={company?.next_invoice_number}
                  onChange={(e) => {
                    if (Number(e.target.value)) setCompany({ ...company, next_invoice_number: e.target.value });
                  }}
                />
              </Popover>
            </Col>
            <Col span={6}>
              <Popover placement="top" content={<div>This is the default number of days until an invoice is due.</div>}>
                <label className="form-label">Default Days To Pay</label>

                <Input
                  type="text"
                  placeholder="Enter Default Payment Term Here"
                  value={company?.default_payment_term}
                  onChange={(e) => {
                    if (Number(e.target.value)) setCompany({ ...company, default_payment_term: e.target.value });
                  }}
                />
              </Popover>
            </Col>
            <Col span={6}>
              <Popover
                placement="top"
                content={
                  <div>
                    This is the rounding interval used with the timer.
                    <br />
                    Time Entries created using the timer will have the hours rounded to this interval.
                  </div>
                }
              >
                <label className="form-label">Timer Rounding Interval</label>

                <Select
                  value={timer_rounding_intervals.find((f) => f.value === company?.rounding_interval) || null}
                  options={timer_rounding_intervals}
                  onChange={(e) => {
                    setCompany({ ...company, rounding_interval: e.value });
                  }}
                />
              </Popover>
            </Col>
            <Col span={6}></Col>

            <Col span={6}>
              <label className="form-label">Job Start Status</label>
              <Select
                value={jobStatuses.find((f) => f.start === 1) ? jobStatuses.find((f) => f.start === 1) : null}
                options={jobStatuses}
                onChange={(e) => {
                  setJobStatuses(
                    jobStatuses.map((m) => {
                      if (m.id === e.value) return { ...m, start: 1 };
                      return { ...m, start: 0 };
                    }),
                  );
                }}
              />
            </Col>
            <Col span={6}>
              <label className="form-label">Job End Status</label>
              <Select
                value={jobStatuses.find((f) => f.end === 1) ? jobStatuses.find((f) => f.end === 1) : null}
                options={jobStatuses}
                onChange={(e) => {
                  setJobStatuses(
                    jobStatuses.map((m) => {
                      if (m.id === e.value) return { ...m, end: 1 };
                      return { ...m, end: 0 };
                    }),
                  );
                }}
              />
            </Col>
            <Col span={6}>
              <label className="form-label">Task Start Status</label>
              <Select
                value={taskStatuses.find((f) => f.start === 1) ? taskStatuses.find((f) => f.start === 1) : null}
                options={taskStatuses}
                onChange={(e) => {
                  setTaskStatuses(
                    taskStatuses.map((m) => {
                      if (m.id === e.value) return { ...m, start: 1 };
                      return { ...m, start: 0 };
                    }),
                  );
                }}
              />
            </Col>
            <Col span={6}>
              <label className="form-label">Task End Status</label>
              <Select
                value={taskStatuses.find((f) => f.end === 1) ? taskStatuses.find((f) => f.end === 1) : null}
                options={taskStatuses}
                onChange={(e) => {
                  setTaskStatuses(
                    taskStatuses.map((m) => {
                      if (m.id === e.value) return { ...m, end: 1 };
                      return { ...m, end: 0 };
                    }),
                  );
                }}
              />
            </Col>

            <Col span={24}>
              <hr />
            </Col>
            <Col span={12}>
              <Popover placement="top" content={<div>This text will be displayed at the bottom of your invoices.</div>}>
                <label className="form-label">Invoice Footer</label>

                <ReactQuill
                  theme="snow"
                  value={company?.invoice_footer_text}
                  onChange={(e) => {
                    setCompany({ ...company, invoice_footer_text: e });
                  }}
                />
              </Popover>
            </Col>
            <Col span={12}>
              <Popover placement="top" content={<div>This text will go at the bottom of emails sent when emailing invoices to clients.</div>}>
                <label className="form-label">Email Footer</label>

                <ReactQuill
                  theme="snow"
                  modules={modules}
                  formats={formats}
                  value={company?.email_footer_text}
                  onChange={(e) => {
                    setCompany({ ...company, email_footer_text: e });
                  }}
                />
              </Popover>
            </Col>

            <Col span={24} className="text-right">
              <Button type="primary" onClick={SaveCompanyMiscSettings}>
                <FontAwesomeIcon icon="save" className="pe-none mr-1" />
                &nbsp;Save Settings
              </Button>
            </Col>
          </Row>
        </>
      ),
    },
  ];

  return (
    <main className="content">
      <div className="container-fluid">
        <div className="header">
          <h1 className="header-title">Company Settings</h1>
        </div>
        <Card>
          <Tabs tabPosition="left" size="large" items={company_setting_tabs} />
        </Card>
      </div>
      <Modal
        open={invoiceModalOpen}
        width={"240mm"}
        title={<Typography.Title level={5}>View Invoice</Typography.Title>}
        onCancel={() => {
          setInvoiceModalOpen(false);
        }}
        footer={
          <Space>
            <Button
              onClick={() => {
                setInvoiceModalOpen(false);
              }}
            >
              Cancel
            </Button>
            <ReactToPrint
              trigger={() => {
                return <Button type="primary">Print/Save Invoice</Button>;
              }}
              content={() => invoiceRef.current}
            />
          </Space>
        }
      >
        <Card style={{ width: "210mm", minHeight: "260mm", margin: "auto", pageBreakAfter: "always" }} ref={invoiceRef}>
          <div className="card-body" style={{ backgroundColor: "white" }}>
            <Row gutter={[16, 16]}>
              <Col span={6} style={{ display: "flex", alignItems: "center", fontSize: "14px" }}>
                <div style={{ maxWidth: "105px", display: "inline-block" }}>
                  <img
                    crossOrigin="anonymous"
                    src="https://firmtamer-public-bucket.s3.amazonaws.com/firmtamer-mascot-no-bg.png"
                    style={{ maxWidth: "95px", maxHeight: "99%" }}
                    id="user-info-image"
                    className="img-fluid mb-2"
                    alt="User"
                  />
                </div>
                <div style={{ display: "inline-block", marginLeft: "15px" }}></div>
              </Col>
              <Col span={12} className="text-center">
                <h3>INVOICE #{invoice?.subscription_id}</h3>
              </Col>
              <Col span={6} className="text-right">
                <strong>FirmTamer</strong>
                <p>
                  www.firmtamer.com
                  <br></br>
                  (833) 347-6826
                  <br></br>
                </p>
              </Col>

              <Col span={24}>
                <hr className="my-4"></hr>
              </Col>
              <Col span={12} style={{ marginLeft: ".9in" }}>
                {/* <div className="text-muted">Client</div> */}
                <strong>{invoice?.company_name}</strong>
                <p>
                  {props.MakeDateTimeReadable(invoice?.start_date)} - {props.MakeDateTimeReadable(invoice?.end_date)}
                  <br></br>
                </p>
              </Col>
            </Row>
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>Description</th>
                  <th className="text-end">Price</th>
                  <th className="text-end">Amount</th>
                  <th className="text-end">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>First User</td>
                  <td className="text-end">{props.FormatNumberToCurrency(invoice?.price_initial_user)}</td>
                  <td className="text-end">1</td>
                  <td className="text-end">{props.FormatNumberToCurrency(invoice?.price_initial_user)}</td>
                </tr>
                {invoice?.user_count > 1 ? (
                  <tr>
                    <td>Additional User</td>
                    <td className="text-end">{props.FormatNumberToCurrency(invoice?.price_additional_users)}</td>
                    <td className="text-end">{invoice?.user_count - 1}</td>
                    <td className="text-end">{props.FormatNumberToCurrency(invoice?.price_additional_users * (invoice?.user_count - 1))}</td>
                  </tr>
                ) : null}
                {invoice?.discount > 0 ? (
                  <tr>
                    <td>Discount</td>
                    <td></td>
                    <td></td>
                    <td className="text-end">-{props.FormatNumberToCurrency(invoice?.discount)}</td>
                  </tr>
                ) : null}
              </tbody>
              <tfoot>
                <tr>
                  <th>Total:</th>
                  <th></th>
                  <th></th>
                  <th className="text-end">{props.FormatNumberToCurrency(invoice?.final_price)}</th>
                </tr>
              </tfoot>
            </table>
            <div className="text-center">
              <p className="text-sm">
                <strong>Thank you for your business!</strong>
              </p>
            </div>
          </div>
        </Card>
      </Modal>
      {/* QBO Account Link Modal */}
      <Modal
        open={qboAccountsModalOpen}
        footer={
          <Space>
            <Button
              onClick={() => {
                setQboAccountsModalOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button type="primary" data-bs-dismiss="modal" onClick={ImportQBOAccounts}>
              Pull Accounts
            </Button>
          </Space>
        }
      >
        <p>
          Below is a list of all your QuickBooks Accounts. <br />
          <br />
          We will import this list as a read-only list into FirmTamer, so that you can link your items to Accounts and they sync in QuickBooks Online.
        </p>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <List
              header={<Typography.Title level={5}>QuickBooks Online Accounts</Typography.Title>}
              bordered
              dataSource={qboAccounts}
              renderItem={(item) => (
                <List.Item>
                  <Typography.Text>{item.FullyQualifiedName}</Typography.Text>
                </List.Item>
              )}
            />
          </Col>
        </Row>
      </Modal>
      {/* QBO Client Link Modal */}
      <Modal
        open={qboClientsModalOpen}
        width={1200}
        footer={
          <Space>
            <Button
              onClick={() => {
                setQboClientsModalOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button type="primary" data-bs-dismiss="modal" onClick={ImportQBOClients}>
              Sync Clients
            </Button>
          </Space>
        }
      >
        <Row gutter={[16, 16]}>
          <Col span={24} className="text-center">
            <Typography.Title level={5}>
              For best results, make sure the client names match in both QuickBooks Online and FirmTamer and refresh this page to get all the clients into the left list before clicking 'Sync Clients'.
            </Typography.Title>
          </Col>
          <Col span={12}>
            <List
              header={
                <Typography.Title level={5}>
                  Clients With Matching Names in Both Systems <br /> (No Action Required)
                </Typography.Title>
              }
              bordered
              dataSource={qboClients.filter((f) => ftClients.find((ftf) => f.processed_name === ftf.processed_name))}
              renderItem={(item) => (
                <List.Item>
                  <Typography.Text>{item.DisplayName}</Typography.Text>
                </List.Item>
              )}
            />
          </Col>
          <Col span={12}>
            <List
              header={
                <Typography.Title level={5}>
                  Clients Without Matching Names <br />
                  (A New Client Will Be Created In FirmTamer)
                </Typography.Title>
              }
              bordered
              dataSource={qboClients.filter((f) => typeof ftClients.find((ftf) => f.processed_name === ftf.processed_name) === "undefined")}
              renderItem={(item) => (
                <List.Item>
                  <Typography.Text>{item.DisplayName}</Typography.Text>
                </List.Item>
              )}
            />
          </Col>
        </Row>
      </Modal>
      {/* QBO Item Link Modal */}
      <Modal
        open={qboItemsModalOpen}
        width={modalWidth}
        title="QuickBooks Online Items Link"
        onCancel={() => {
          setQboItemsModalOpen(false);
        }}
        footer={
          <Space>
            <Button
              onClick={() => {
                setQboItemsModalOpen(false);
              }}
            >
              Close
            </Button>
            <Button
              type="primary"
              disabled={items.filter((f) => typeof qboItems.find((ftf) => f.processed_name === ftf.processed_name) === "undefined").filter((f) => !f.qbo_account_id).length > 0}
              onClick={UpdateItemsFromQBO}
            >
              Link Items
            </Button>
          </Space>
        }
      >
        <div className="text-center">
          <Typography.Title level={5}>
            For best results, make sure the item names match in both QuickBooks Online and FirmTamer and refresh this page to get all the items into the right list before clicking 'Link Items'.
          </Typography.Title>
          <div className="text-center mt-2">
            <h4>QuickBooks Online Items</h4>
          </div>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Typography.Title level={5}>
                Items With Matching Names in Both Systems <br /> (No Action Required)
                <Tooltip title="No action required for this column. Items in this column will sync with QuickBooks Online.">
                  <FontAwesomeIcon className="ml-2" icon={faQuestionCircle} style={{ cursor: "pointer" }} />
                </Tooltip>
              </Typography.Title>
            </Col>
            <Col span={12}>
              <Typography.Title level={5}>
                Items Without Matching Names
                <Tooltip title="Items in this column need to be resolved before continuing. If the item is in QuickBooks Online, an item will be created in FirmTamer. If the item is in FirmTamer, the item must have a QuickBooks Online account assigned to it before this linking process can be done. You can add a QuickBooks Online Account by editing the item.">
                  <FontAwesomeIcon className="ml-2" icon={faQuestionCircle} style={{ cursor: "pointer" }} />
                </Tooltip>
              </Typography.Title>
            </Col>
            <Col span={12}>
              <Input.TextArea
                className="form-control"
                readOnly
                rows="20"
                w="100%"
                value={qboItems
                  .filter((f) => items.find((ftf) => f.processed_name === ftf.processed_name))
                  .map((qboc) => {
                    return qboc.Name;
                  })
                  .join("\n")}
              />
            </Col>
            <Col span={12}>
              <Input.TextArea
                className="form-control"
                readOnly
                rows="20"
                w="100%"
                value={
                  qboItems
                    .filter((f) => typeof items.find((ftf) => f.processed_name === ftf.processed_name) == "undefined")
                    .map((qboc) => {
                      return qboc.Name + " (Found In QuickBooks)";
                    })
                    .join("\n") +
                  "\n\n" +
                  items
                    .filter((f) => typeof qboItems.find((ftf) => f.processed_name === ftf.processed_name) == "undefined")
                    .map((ft_item) => {
                      return ft_item.name + " (Found In FirmTamer)";
                    })
                    .join("\n")
                }
              />
            </Col>
          </Row>
        </div>
      </Modal>
    </main>
  );
}

export default CompanySettings;
